import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useLocation } from '@reach/router';
import { useStaticQuery, graphql } from 'gatsby';

// https://www.gatsbyjs.com/docs/add-seo-component/

const Head = ({ canonicalPath, description, image, jsonLd, noIndex, title }) => {
  const { pathname } = useLocation();

  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            defaultTitle: title
            defaultDescription: description
            siteUrl
            pathPrefix
            defaultImage: image
            twitterUsername
          }
        }
      }
    `,
  );

  const {
    defaultTitle,
    defaultDescription,
    siteUrl,
    pathPrefix,
    defaultImage,
    twitterUsername,
  } = site.siteMetadata;

  const siteBaseUrl = `${siteUrl.replace(/\/$/, '')}${pathPrefix || ''}`;
  const unprefixedPath =
    pathPrefix && pathname.startsWith(pathPrefix)
      ? pathname.slice(pathPrefix.length) || '/'
      : pathname;
  const routePath = canonicalPath || unprefixedPath;
  const normalizedPath = routePath.startsWith('/') ? routePath : `/${routePath}`;
  const canonicalUrl = `${siteBaseUrl}${normalizedPath}`;
  const imagePath = image || defaultImage;
  const imageUrl = /^https?:\/\//.test(imagePath)
    ? imagePath
    : `${siteBaseUrl}${imagePath.startsWith('/') ? imagePath : `/${imagePath}`}`;

  const seo = {
    title: title || defaultTitle,
    description: description || defaultDescription,
    image: imageUrl,
    url: canonicalUrl,
  };

  return (
    <Helmet title={title} defaultTitle={seo.title} titleTemplate={`%s | ${defaultTitle}`}>
      <html lang="en" />

      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />
      <meta name="author" content="Shikhar Agnihotri" />
      <meta
        name="robots"
        content={
          noIndex
            ? 'noindex, follow'
            : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
        }
      />
      <link rel="canonical" href={seo.url} />
      <link
        rel="alternate"
        type="text/plain"
        href={`${siteBaseUrl}/llms.txt`}
        title="Agent-readable profile"
      />

      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:image:alt" content="Shikhar Agnihotri portfolio" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={seo.url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={defaultTitle} />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterUsername} />
      <meta name="twitter:creator" content={twitterUsername} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      <meta name="twitter:image:alt" content="Shikhar Agnihotri portfolio" />

      <meta name="google-site-verification" content="DCl7VAf9tcz6eD9gb67NfkNnJ1PKRNcg8qQiwpbx9Lk" />
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};

export default Head;

Head.propTypes = {
  canonicalPath: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  jsonLd: PropTypes.object,
  noIndex: PropTypes.bool,
};

Head.defaultProps = {
  canonicalPath: null,
  title: null,
  description: null,
  image: null,
  jsonLd: null,
  noIndex: false,
};
